export const EMPTY_HSE_DASHBOARD = {
  kpis: {
    events: 0,
    deviations: 0,
    unread_docs: 0,
    open_actions: 0,
    overdue: 0,
  },
  my_tasks: [],
  alerts: [],
  recent_events: [],
};

export const HSE_DASHBOARD_PATHS = [
  "/hse/dashboard",
  "/hse_ks/dashboard",
];

const CLOSED_ACTION_STATUSES = new Set(["completed", "cancelled"]);
const TASK_KINDS = new Set(["action", "acknowledgement", "task"]);
const ALERT_SEVERITIES = new Set(["error", "warning", "info"]);

export function toCount(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function startOfDay(date = new Date()) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function isOpenActionStatus(status) {
  return !CLOSED_ACTION_STATUSES.has(String(status || "").toLowerCase());
}

export function isOverdueDeadline(deadline, now = new Date()) {
  if (!deadline) return false;
  const parsed = new Date(deadline);
  return Number.isFinite(parsed.getTime()) && parsed < startOfDay(now);
}

export function isOverdueAction(action, now = new Date()) {
  if (!action || !isOpenActionStatus(action.status)) return false;
  return isOverdueDeadline(action.deadline, now);
}

export function isDueSoonAction(action, now = new Date(), days = 7) {
  if (!action || !isOpenActionStatus(action.status) || !action.deadline) return false;
  const parsed = new Date(action.deadline);
  if (!Number.isFinite(parsed.getTime())) return false;
  const start = startOfDay(now);
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return parsed >= start && parsed <= end;
}

export function eventHref(id) {
  return id != null ? `/dashboard/hse-ks/events/${id}` : "/dashboard/hse-ks/events";
}

export function deviationHref(id) {
  return id != null
    ? `/dashboard/hse-ks/deviations/${id}`
    : "/dashboard/hse-ks/deviations";
}

export function recentEventHref(item) {
  const kind = String(item?.kind || item?.type || "").toLowerCase();
  if (kind === "deviation") return deviationHref(item?.id);
  return eventHref(item?.id);
}

export function documentHref(id) {
  return id != null
    ? `/dashboard/hse-ks/documents/${id}`
    : "/dashboard/quality/employee-handbook";
}

export function actionHref(action) {
  const actionId = action?.id ?? action?.hse_action_id;
  return actionId != null
    ? `/dashboard/hse-ks/tiltak/${actionId}`
    : "/dashboard/hse-ks/tiltak";
}

export function safeDashboardHref(href, fallback = "/dashboard/hse-ks/dashboard") {
  if (typeof href !== "string") return fallback;
  const trimmed = href.trim();
  if (trimmed.startsWith("/dashboard/")) return trimmed;
  return fallback;
}

export function unwrapDashboardPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (
    raw.kpis ||
    raw.kpi ||
    raw.my_tasks ||
    raw.tasks ||
    raw.recent_events ||
    raw.alerts ||
    raw.notifications
  ) {
    return raw;
  }
  if (raw.data && typeof raw.data === "object") {
    return unwrapDashboardPayload(raw.data);
  }
  return raw;
}

function normalizeKind(kind) {
  const value = String(kind || "").toLowerCase();
  if (value === "tiltak" || value === "action") return "action";
  if (value === "acknowledgment" || value === "acknowledgement" || value === "document") {
    return "acknowledgement";
  }
  if (TASK_KINDS.has(value)) return value;
  return "task";
}

function normalizeSeverity(severity) {
  const value = String(severity || "").toLowerCase();
  if (ALERT_SEVERITIES.has(value)) return value;
  if (value === "danger" || value === "critical") return "error";
  if (value === "warn") return "warning";
  return "info";
}

export function normalizeTask(item, index = 0) {
  if (!item || typeof item !== "object") return null;
  const id = item.id ?? item.key ?? `task-${index}`;
  const kind = normalizeKind(item.kind || item.type);
  const fallbackHref =
    kind === "acknowledgement"
      ? documentHref(item.document_id || item.id)
      : kind === "action"
        ? actionHref(item)
        : "/dashboard/hse-ks/dashboard";
  return {
    id: String(id),
    kind,
    title: item.title || item.name || "",
    subtitle: item.subtitle || item.description || "",
    due_date: item.due_date || item.deadline || item.event_date || null,
    overdue: Boolean(item.overdue) || isOverdueDeadline(item.due_date || item.deadline),
    href: safeDashboardHref(item.href || item.path, fallbackHref),
  };
}

export function normalizeAlert(item, index = 0) {
  if (!item || typeof item !== "object") return null;
  const id = item.id ?? item.key ?? `alert-${index}`;
  return {
    id: String(id),
    severity: normalizeSeverity(item.severity || item.level),
    title: item.title || item.name || "",
    body: item.body || item.message || item.description || "",
    href: item.href || item.path ? safeDashboardHref(item.href || item.path) : null,
  };
}

export function normalizeRecentEvent(item, index = 0) {
  if (!item || typeof item !== "object") return null;
  const id = item.id ?? `event-${index}`;
  const kind = String(item.kind || item.type || "").toLowerCase() || "hse_event";
  return {
    id,
    kind,
    title: item.title || item.display_title || "",
    event_date: item.event_date || item.date || item.occurred_on || item.created_at || null,
    event_type: item.event_type_display || item.event_type || "",
    status: item.status_display || item.status || "",
    href: safeDashboardHref(item.href, recentEventHref(item)),
  };
}

function mapList(list, mapper) {
  if (!Array.isArray(list)) return [];
  return list.map(mapper).filter(Boolean);
}

export function normalizeHseDashboard(raw) {
  const payload = unwrapDashboardPayload(raw) || {};
  const kpis = payload.kpis || payload.kpi || {};
  return {
    kpis: {
      events: toCount(
        kpis.events ?? kpis.events_count ?? payload.events_count ?? payload.total_events
      ),
      deviations: toCount(
        kpis.deviations ?? kpis.deviations_count ?? payload.deviations_count
      ),
      unread_docs: toCount(
        kpis.unread_docs ?? kpis.unread_documents ?? kpis.unread_docs_count
      ),
      open_actions: toCount(
        kpis.open_actions ?? kpis.open_tiltak ?? kpis.open_actions_count
      ),
      overdue: toCount(kpis.overdue ?? kpis.overdue_actions ?? kpis.overdue_count),
    },
    my_tasks: mapList(payload.my_tasks || payload.tasks || [], normalizeTask),
    alerts: mapList(payload.alerts || payload.notifications || [], normalizeAlert),
    recent_events: mapList(payload.recent_events || payload.events || [], normalizeRecentEvent),
  };
}

export function countOpenActionsFromStats(stats = {}) {
  const byStatus = stats.actions_by_status || {};
  if (typeof stats.total_actions === "number") {
    return Math.max(
      0,
      toCount(stats.total_actions) -
        toCount(byStatus.completed) -
        toCount(byStatus.cancelled)
    );
  }
  return Object.entries(byStatus).reduce((sum, [status, count]) => {
    return isOpenActionStatus(status) ? sum + toCount(count) : sum;
  }, 0);
}

export function unreadDocumentsFromList(documents = []) {
  return (Array.isArray(documents) ? documents : []).filter((doc) => {
    const status = doc?.current_user_read_status;
    if (!status) return true;
    if (status.exempt_as_creator) return false;
    return !status.acknowledged_read_understood;
  });
}

export function buildDerivedAlerts({
  overdueCount = 0,
  unreadCount = 0,
  dueSoonCount = 0,
  criticalEventCount = 0,
} = {}) {
  const alerts = [];
  if (overdueCount > 0) {
    alerts.push({
      id: "overdue-actions",
      severity: "error",
      title: "overdue_actions",
      count: overdueCount,
      href: "/dashboard/hse-ks/tiltak",
    });
  }
  if (unreadCount > 0) {
    alerts.push({
      id: "unread-docs",
      severity: "warning",
      title: "unread_docs",
      count: unreadCount,
      href: "/dashboard/quality/employee-handbook",
    });
  }
  if (dueSoonCount > 0) {
    alerts.push({
      id: "due-soon",
      severity: "info",
      title: "due_soon",
      count: dueSoonCount,
      href: "/dashboard/hse-ks/tiltak",
    });
  }
  if (criticalEventCount > 0) {
    alerts.push({
      id: "critical-events",
      severity: "warning",
      title: "critical_events",
      count: criticalEventCount,
      href: "/dashboard/hse-ks/events",
    });
  }
  return alerts;
}

export function composeFallbackDashboard({
  events = [],
  eventsTotal,
  eventStats = {},
  actions = [],
  actionStats = {},
  deviationsTotal = 0,
  documents = [],
  now = new Date(),
} = {}) {
  const unreadDocs = unreadDocumentsFromList(documents);
  const openUserActions = (Array.isArray(actions) ? actions : []).filter((action) =>
    isOpenActionStatus(action.status)
  );
  const overdueUserActions = openUserActions.filter((action) => isOverdueAction(action, now));
  const dueSoonUserActions = openUserActions.filter((action) => isDueSoonAction(action, now));

  const myTasks = [
    ...openUserActions.map((action, index) =>
      normalizeTask(
        {
          id: `action-${action.id ?? index}`,
          kind: "action",
          title: action.title,
          subtitle: action.status_display || action.priority_display || "",
          due_date: action.deadline,
          overdue: isOverdueAction(action, now),
          href: actionHref(action),
        },
        index
      )
    ),
    ...unreadDocs.map((doc, index) =>
      normalizeTask(
        {
          id: `ack-${doc.id ?? index}`,
          kind: "acknowledgement",
          title: doc.title || doc.document_number,
          subtitle: doc.document_number || "",
          href: documentHref(doc.id),
        },
        index
      )
    ),
  ]
    .filter(Boolean)
    .slice(0, 8);

  const overdueCount = toCount(actionStats.overdue_actions, overdueUserActions.length);
  const kpis = {
    events: toCount(eventStats.total_events, toCount(eventsTotal, events.length)),
    deviations: toCount(deviationsTotal),
    unread_docs: unreadDocs.length,
    open_actions: countOpenActionsFromStats(actionStats) || openUserActions.length,
    overdue: overdueCount,
  };

  return {
    kpis,
    my_tasks: myTasks,
    alerts: buildDerivedAlerts({
      overdueCount: kpis.overdue,
      unreadCount: kpis.unread_docs,
      dueSoonCount: toCount(actionStats.due_soon_actions, dueSoonUserActions.length),
      criticalEventCount: toCount(eventStats.events_by_severity?.critical),
    }),
    recent_events: (Array.isArray(events) ? events : [])
      .slice(0, 8)
      .map((event, index) => normalizeRecentEvent(event, index))
      .filter(Boolean),
  };
}

export function shouldUseDashboardFallback(status) {
  return status == null || status === 404 || status === 403 || status === 501;
}
